import $ from 'jquery';
import React, { Fragment, useState, useEffect } from 'react';
import Select from 'react-select';

import CategoryAndFolderSelector from '../move/CategoryAndFolderSelector';
import TettraEditor from '../../tettra-editor/components/TettraEditor';

import { questionToEditorState, textToEditorState } from '../../tettra-editor/utilities/contentToEditorState';
import editorStateToRawContent from '../../tettra-editor/utilities/editorStateToRawContent';

import { getQuestionUrl, getQuestionsUrl } from '../../utilities/urlHelper';

import useUsers from '../../hooks/useUsers';
import useCategories from '../../hooks/useCategories';

import removeFirstLineFromHtml from '../../tettra-editor/utilities/removeFirstLineFromHtml';
import editorStateToHTML from '../../tettra-editor/utilities/editorStateToHTML';

const EDITOR_CONFIG = {
    enableSmallEditor: true,
    spellCheck: true,
    enableAutosave: false,
    enableTables: false,
    enableInsertControls: false,
    enableMentionSuggestions: true,
    enableInternalSuggestions: true,
    enableExternalSuggestions: true,
    enableFileUpload: false,
};

const LOADING_USER = [{ display_name: 'Loading users....', id: null }];

const SelectAssignees = ({
    assignees, setAssignees, categoryId, isDisabled,
}) => {
    const users = useUsers();
    const categories = useCategories();

    const getCategoryOwnerIds = (categoryId) => categories && categories[categoryId] && categories[categoryId].owner_ids;

    const getOptions = () => {
        if (!users || !categories) return LOADING_USER;

        const categoryOwnerIds = getCategoryOwnerIds(categoryId);
        if (categoryOwnerIds && categoryOwnerIds.length > 0) {
            const ownerUsers = [];
            const nonOwnerUser = [];
            users.filter(u => u.id).forEach((user) => {
                if (categoryOwnerIds.includes(user.id)) {
                    ownerUsers.push(user);
                } else {
                    nonOwnerUser.push(user);
                }
            });

            return ([
                {
                    label: 'Category Owners',
                    options: ownerUsers,
                },
                {
                    label: 'Users',
                    options: nonOwnerUser,
                },
            ]);
        }

        return users.filter(u => u.id);
    };

    return (
        <Fragment>
            <label htmlFor="assignees" className="bold block col-12 mb1">Who can answer this question?</label>
            <Select
                name="assignees"
                className="block col-12 field mb2"
                options={getOptions()}
                placeholder={users ? 'Not sure' : 'Loading'}
                value={assignees}
                onChange={selectedAssignee => setAssignees(selectedAssignee)}
                getOptionValue={option => option.id}
                getOptionLabel={option => option.display_name}
                isMulti
                isClearable
                isDisabled={isDisabled}
            />
        </Fragment>
    );
};

const CreateQuestionModal = ({
    question, initialTitle, initialDescription, initialCategoryId, initialFolderId, initialAssignee, closeModal,
}) => {
    const users = useUsers();
    initialAssignee = initialAssignee ? JSON.parse(initialAssignee) : null;
    const [title, setTitle] = useState(question ? question.title : initialTitle || '');
    const [editorState, setEditorState] = useState(question ? questionToEditorState(question) : textToEditorState(initialDescription));
    const [isUploading, setUploading] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(null);
    const [categoryId, setCategoryId] = useState(question ? question.category_id : initialCategoryId || null);
    const [folderId, setFolderId] = useState(question ? question.folder_id : initialFolderId || null);
    const [assignees, setAssignees] = useState(initialAssignee || null);

    useEffect(() => setAssignees(question && users && users.filter(u => question.assignee_ids.includes(u.id))), [users]);

    const onMouseDownModal = ({ target }) => {
        const className = target.getAttribute('class');
        if (className && className.includes('react-modal open')) {
            closeModal();
        }
    };

    const buttonClass = (isUploading || isSubmitting) ? 'is-disabled' : '';
    const buttonText = isSubmitting
        ? (question ? 'Updating....' : 'Creating...')
        : (question ? 'Update question' : 'Create question');
    const headerText = question ? 'Edit question' : 'Ask a question';

    const createOrUpdateQuestion = (e) => {
        e.preventDefault();
        if (isUploading || isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        const URL = question ? getQuestionUrl(question.id) : getQuestionsUrl();
        const contentStateJSON = initialDescription ? JSON.stringify(removeFirstLineFromHtml(editorStateToRawContent(editorState))) : JSON.stringify(editorStateToRawContent(editorState));
        const html = editorStateToHTML(editorState);
        const method = question ? 'PUT' : 'POST';

        const $publishQuestionForm = $(`<form method="POST" action="${URL}"></form>`)
            .append(`<input name="_token" type="hidden" value="${window.csrfToken}" />`)
            .append(`<input name="_method" type="hidden" value="${method}" />`)
            .append($('<textarea name="title" />').text(title))
            .append($('<textarea name="details_content_state" />').text(contentStateJSON))
            .append($('<textarea name="details_html" />').text(html));

        if (categoryId) {
            $publishQuestionForm.append(`<input name="category_id" value="${categoryId}" />`);
        }

        if (folderId) {
            $publishQuestionForm.append(`<input name="folder_id" value="${folderId}" />`);
        }

        if (initialAssignee != null) {
            $publishQuestionForm.append(`<input name="assignees[]" value="${initialAssignee.id}" />`);
        } else if (assignees) {
            assignees.forEach((assignee) => {
                $publishQuestionForm.append(`<input name="assignees[]" value="${assignee.id}" />`);
            });
        }

        $publishQuestionForm
            .hide()
            .appendTo('body')
            .submit();
    };

    return (
        <div className="react-modal open move-folder-modal" onMouseDown={onMouseDownModal}>
            <div className="modal-content pt">
                <h2>{headerText}</h2>
                <label htmlFor="title" className="bold block col-12 mb1">What do you want to know?</label>
                <input
                    className="block col-12 mb2 field"
                    placeholder="e.g. What can I expense? How do I close the office?"
                    name="title"
                    type="text"
                    id="title"
                    value={title}
                    onChange={({ target }) => setTitle(target.value)}
                    tabIndex={0}
                />
                <label htmlFor="tettra-editor" className="bold block col-12 mb1">Add more details</label>

                <div className="question-editor mb2">
                    <TettraEditor
                        config={EDITOR_CONFIG}
                        editorState={editorState}
                        updateModel={setEditorState}
                        setUploading={setUploading}
                    />
                </div>
                <CategoryAndFolderSelector
                    allowUncategorize
                    initialCategoryId={categoryId}
                    initialFolderId={folderId}
                    setCategoryId={setCategoryId}
                    setFolderId={setFolderId}
                    selectOnly
                />
                <SelectAssignees
                    assignees={assignees}
                    setAssignees={setAssignees}
                    categoryId={categoryId}
                    isDisabled={!!initialAssignee}
                />
                <div className="right-align mt2">
                    <span className="btn-secondary mr1" aria-label="Close" role="button" onClick={closeModal}>Cancel</span>
                    <span className={`btn btn-primary ${buttonClass}`} aria-label="create question" role="button" onClick={createOrUpdateQuestion}>{buttonText}</span>
                </div>
            </div>
        </div>
    );
};

export default CreateQuestionModal;
